(function () {
  'use strict';

  // Configuration injected via Liquid before this script:
  // <script>
  //   window.CartConfig = {
  //     moneyFormat: "{{ shop.money_format }}",
  //     locale: "{{ shop.locale }}",
  //     currency: "{{ shop.currency }}"
  //   };
  // </script>
  const {
    moneyFormat = '',
    locale = window.CartConfig?.locale || 'en',
    currency = window.CartConfig?.currency || 'USD'
  } = window.CartConfig || {};

  /**
   * Formats an amount in cents to a localized currency string.
   */
  function formatMoney(cents) {
    if (window.theme?.currency?.formatMoney) {
      return theme.currency.formatMoney(cents, moneyFormat);
    }
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  }





  /**
   * Frequently Bought Together: adds selected variants as a batch.
   */
  const FBTModule = {
    init() {
      const checkboxes = [...document.querySelectorAll('.fbt-checkbox')];
      const totalEl = document.getElementById('fbt-total');
      const addBtn = document.getElementById('fbt-add');
      const errorEl = document.getElementById('fbt-error');

      if (!checkboxes.length || !totalEl || !addBtn || !errorEl) {
        return;
      }

      const updateButtonState = () => {
        const anyChecked = checkboxes.some(cb => cb.checked);
        addBtn.disabled = !anyChecked;
        if (anyChecked) errorEl.style.display = 'none';
      };

      const recalc = () => {
        const sum = checkboxes.reduce((acc, cb) => cb.checked ? acc + parseInt(cb.dataset.price, 10) : acc, 0);
        totalEl.textContent = formatMoney(sum);
        updateButtonState();
      };

      checkboxes.forEach(cb => cb.addEventListener('change', recalc));
      recalc();

      addBtn.addEventListener('click', () => {
        if (addBtn.disabled) return;

        const items = checkboxes
          .filter(cb => cb.checked)
          .map(cb => ({ id: cb.dataset.variantId, quantity: 1 }));

        if (!items.length) {
          errorEl.style.display = 'block';
          return;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        })
          .then(res => res.ok ? res.json() : Promise.reject('Add pack failed'))
          .then(() => typeof window.fetchCart === 'function' && window.fetchCart())
          .catch(err => {
            console.error(err);
            errorEl.textContent = 'Hubo un problema al añadir los productos.';
            errorEl.style.display = 'block';
          });
      });
    }
  };

  /**
   * Aviso de Epic Pro Assembly en el minicarrito.
   *
   * /cart.js no devuelve los tags del producto, y son los tags (bikeassembly /
   * ebikeassembly) los que dicen si algo es una bici — product_type no sirve
   * porque conviven dos taxonomías en el catálogo.
   *
   * Partimos de lo que Liquid ya serializó en window.CartWithProduct (viene con
   * el producto completo, tags incluidos) y solo pedimos por HTTP los handles
   * que falten. En la práctica eso es cero peticiones al cargar y una sola al
   * añadir un producto nuevo.
   */
  const assemblyTags = new Map();

  function cacheTagsFromRenderedCart() {
    (window.CartWithProduct || []).forEach(entry => {
      const product = entry && entry.product;
      if (product && product.handle && Array.isArray(product.tags)) {
        assemblyTags.set(product.handle, product.tags.map(t => String(t).toLowerCase()));
      }
    });
  }

  async function cartHasBike(cartData) {
    cacheTagsFromRenderedCart();

    const missing = [...new Set(
      (cartData.items || [])
        .map(item => item.handle)
        .filter(handle => handle && !assemblyTags.has(handle))
    )];

    await Promise.all(missing.map(async handle => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        const product = await res.json();
        assemblyTags.set(handle, (product.tags || []).map(t => String(t).toLowerCase()));
      } catch (_) {
        // Cacheamos vacío a propósito: si el producto no se puede leer, no
        // queremos reintentarlo en cada render del carrito.
        assemblyTags.set(handle, []);
      }
    }));

    return (cartData.items || []).some(item => {
      const tags = assemblyTags.get(item.handle) || [];
      return tags.includes('bikeassembly') || tags.includes('ebikeassembly');
    });
  }

  function refreshAssemblyNote(cartData) {
    const note = document.querySelector('.mini-cart__assembly-note');
    if (!note) return;

    cartHasBike(cartData)
      .then(hasBike => { note.hidden = !hasBike; })
      .catch(() => { note.hidden = true; });
  }

  /**
   * Mini Cart: fetches and renders cart contents via AJAX.
   */
  const MiniCartModule = {
    init() {
      const trigger = document.querySelector('.mini-cart__trigger');
      const overlay = document.querySelector('.overlay');
      const badge = document.querySelector('.cart-count-badge');
      const removeAllBtn = document.querySelector('.mini-cart__remove-all');
      const itemsContainer = document.querySelector('.mini-cart__items');
      const titleEl = document.querySelector('.mini-cart__title');
      const totalEl = document.querySelector('.mini-cart__total-value');
      let countProducts = 0;
      let isRendering = false; // Flag para prevenir renders simultáneos
      let isFetching = false; // Flag para prevenir múltiples fetches simultáneos

      const fetchCart = async () => {
        // Prevenir múltiples fetches simultáneos
        if (isFetching) {
          console.log('⚠️ Fetch ya en progreso, saltando...');
          return;
        }

        isFetching = true;

        // showLoading() estaba AQUI, fuera del try. Si lanzaba (por ejemplo con alguna
        // de sus tres referencias a null), isFetching se quedaba en true para siempre y
        // el minicart no volvia a refrescarse hasta recargar: todas las llamadas
        // posteriores caian en el "Fetch ya en progreso, saltando...". Ahora va dentro.
        try {
          showLoading();

          // Sin timeout, una peticion que no resuelve ni falla dejaba la llave tomada
          // indefinidamente. Con AbortController el peor caso es reintentar en 10s.
          let respuesta;
          if (typeof AbortController === 'function') {
            const ctrl = new AbortController();
            const corte = setTimeout(() => ctrl.abort(), 10000);
            try {
              respuesta = await fetch('/cart.js', { signal: ctrl.signal });
            } finally {
              clearTimeout(corte);
            }
          } else {
            respuesta = await fetch('/cart.js');
          }

          const data = await respuesta.json();

          renderCart(data);
          return data;
        } catch (error) {
          console.error('Error fetching cart:', error);
          hideLoading();
        } finally {
          isFetching = false;
        }
      };
      
      function showLoading() {
        // Las tres referencias se capturan una sola vez en init(). Si el DOM del
        // minicart se sustituye, quedan colgando y esto lanzaba.
        if (!itemsContainer) return;
        if (titleEl) titleEl.textContent = 'CART';
        if (totalEl) totalEl.textContent = '---';

        itemsContainer.innerHTML = `
          <div class="mini-cart__loading" style="text-align: center; padding: 40px 20px;">
            <div class="loading-spinner" style="margin: 0 auto 16px; width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #333; border-radius: 50%; animation: spin 1s linear infinite;"></div>
            <p style="color: #666; font-size: 14px;">Loading cart...</p>
          </div>
        `;
      }
      
      function hideLoading() {
        if (!itemsContainer) return;
        const loadingEl = itemsContainer.querySelector('.mini-cart__loading');
        if (loadingEl) {
          loadingEl.remove();
        }
      }

      function renderCart(data) {
        // Prevenir renders simultáneos
        if (isRendering) {
          console.log('⚠️ Render ya en progreso, saltando...');
          return;
        }
        
        isRendering = true;
        
        try {
          // Ocultar loading si existe
          hideLoading();
          
          titleEl.textContent = `CART (${data.item_count})`;
          itemsContainer.innerHTML = '';
          totalEl.textContent = formatMoney(data.total_price);

          let countProducts = 0;

          if (data.items.length) {
            data.items.forEach(item => {
              const imgUrl = item.featured_image?.url || '';
              const imgAlt = item.featured_image?.alt || '';

              countProducts = countProducts + item.quantity;
              // Buscar el item correspondiente en CartWithProduct para obtener el compare_at_price
              const cartProduct = window.CartWithProduct?.find(cartItem => cartItem.key === item.key);
              const compareAtPrice = cartProduct?.money_compare_at_price || '';

              // A11y: construir aria-label resumen para el item (título + precio + cantidad)
              const itemA11yLabel = compareAtPrice
                ? `${item.product_title}, quantity ${item.quantity}, sale price ${formatMoney(item.price)}, original price ${compareAtPrice}`
                : `${item.product_title}, quantity ${item.quantity}, price ${formatMoney(item.price)}`;
              itemsContainer.insertAdjacentHTML('beforeend', `
                <div class="mini-cart__item" role="group" aria-label="${itemA11yLabel.replace(/"/g, '&quot;')}">
                  <div class="item__details">
                    <img class="item__image" src="${imgUrl}" alt="" aria-hidden="true">
                    <div class="item__info">
                      <span class="item__title">${item.product_title}${(item.quantity > 1 ? ` x${item.quantity}` : '')}</span>
                      <span class="item__price" aria-hidden="true">${formatMoney(item.price)}</span>
                      ${compareAtPrice ? `<span class="item__old_price" aria-hidden="true">${compareAtPrice}</span>` : ''}
                    </div>
                  </div>
                  <div class="item__actions">
                    <button class="item__remove" data-line-key="${item.key}" aria-label="Remove ${item.product_title.replace(/"/g, '&quot;')} from cart" type="button">
                      <iconify-icon icon="ei:close" aria-hidden="true"></iconify-icon>
                    </button>
                  </div>
                </div>
              `);
            });
            attachRemoveHandlers();
          } else {
            itemsContainer.innerHTML = '<p class="mini-cart__empty">Cart is empty</p>';
          }

          refreshAssemblyNote(data);

          if (data.item_count > 0) {
            badge.textContent = countProducts;
            badge.style.display = 'inline-block';
          } else {
            badge.style.display = 'none';
          }

          // A11y: actualizar texto y aria-label para lectores de pantalla
          const a11yCount = document.querySelector('[data-cart-count-a11y]');
          if (a11yCount) {
            const label = data.item_count === 1 ? '1 item in cart' : `${data.item_count} items in cart`;
            a11yCount.textContent = label;
          }
          const cartTrigger = document.querySelector('.mini-cart__trigger');
          if (cartTrigger) {
            const label = data.item_count === 0
              ? 'Open shopping cart, empty'
              : data.item_count === 1
                ? 'Open shopping cart, 1 item'
                : `Open shopping cart, ${data.item_count} items`;
            cartTrigger.setAttribute('aria-label', label);
          }
        } finally {
          isRendering = false;
        }
      }

      function attachRemoveHandlers() {
        document.querySelectorAll('.item__remove').forEach(btn => {
          const key = btn.dataset.lineKey;
          btn.addEventListener('click', () => updateItem(key, 0));
        });
      }

      function updateItem(lineKey, qty) {
        // Mostrar loading mientras se actualiza
        showLoading();
        
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: qty })
        })
          .then(res => res.json())
          .then(data => {
            // Actualizar window.CartWithProduct después del cambio
            updateCartWithProduct();
            
            // Publicar evento para sincronizar con cart page
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { 
                source: 'minicart', 
                cartData: data 
              });
            }
          })
          .catch(error => {
            console.error('Error updating cart item:', error);
            hideLoading();
          });
      }

      // Función para actualizar window.CartWithProduct
      function updateCartWithProduct() {
        fetch('/cart.js')
          .then(res => res.json())
          .then((cartData) => {
            // OPTIMIZACIÓN: No enriquecer con datos de productos
            // Actualizar window.CartWithProduct con los datos básicos del carrito
            window.CartWithProduct = cartData.items.map(item => {
              const existingCartProduct = window.CartWithProduct?.find(cp => cp.key === item.key);
              return {
                key: item.key,
                qty: item.quantity,
                product: existingCartProduct?.product || {},
                final_line_price: item.final_line_price,
                original_line_price: item.original_line_price,
                money_price: existingCartProduct?.money_price || formatMoney(item.price),
                money_compare_at_price: existingCartProduct?.money_compare_at_price || '',
                total_price: existingCartProduct?.total_price || formatMoney(item.final_line_price)
              };
            });
          });
      }

      // A11y: focus management
      const miniCartEl = document.querySelector('.mini-cart');
      const closeBtn = document.querySelector('.mini-cart__close');
      let lastFocusedBeforeOpen = null;

      function getCartFocusables() {
        return Array.from(miniCartEl.querySelectorAll('a, button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(el => el.offsetParent !== null || el === miniCartEl);
      }

      function openCart() {
        syncMiniCartGeometry();
        lastFocusedBeforeOpen = document.activeElement;
        trigger.classList.add('active');
        overlay.classList.add('overlay--show');
        miniCartEl.classList.add('mini-cart--show');
        miniCartEl.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        // Mover foco al primer focusable del minicart después de que se renderice
        setTimeout(() => {
          const focusables = getCartFocusables();
          if (focusables.length > 0) {
            focusables[0].focus();
          } else {
            miniCartEl.focus();
          }
        }, 100);
        document.addEventListener('keydown', handleCartKeydown);
      }

      function closeCart(restoreFocus = true) {
        // El foco tiene que salir ANTES de marcar aria-hidden. Si sigue dentro, el
        // navegador rechaza el atributo y avisa ("Blocked aria-hidden on an element
        // because its descendant retained focus"). Antes el foco se devolvia al final.
        if (miniCartEl.contains(document.activeElement)) {
          if (restoreFocus && lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
            try { lastFocusedBeforeOpen.focus(); } catch (_) { /* ya no existe en el DOM */ }
          } else if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
          }
        } else if (restoreFocus && lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
          try { lastFocusedBeforeOpen.focus(); } catch (_) { /* ya no existe en el DOM */ }
        }

        trigger.classList.remove('active');
        overlay.classList.remove('overlay--show');
        miniCartEl.classList.remove('mini-cart--show');
        miniCartEl.setAttribute('aria-hidden', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('keydown', handleCartKeydown);
        lastFocusedBeforeOpen = null;
      }

      function handleCartKeydown(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          closeCart(true);
          return;
        }
        // Atrapar Tab dentro del minicart
        if (e.key === 'Tab') {
          const focusables = getCartFocusables();
          if (focusables.length === 0) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }

      trigger.addEventListener('click', async () => {
        // La fuente de verdad es el panel. Antes se leia trigger.classList.contains('active'),
        // que lo escriben a mano las aperturas automaticas: si una fallaba a medias, el icono
        // se quedaba creyendo que estaba abierto y el primer toque no abria, cerraba.
        const isOpen = miniCartEl.classList.contains('mini-cart--show');
        if (isOpen) {
          closeCart(false);
        } else {
          openCart();
          // Mostrar loading inmediatamente y esperar a que fetchCart termine
          await fetchCart();
          // Re-focus al primer focusable después del fetch (ya puede tener más items)
          const focusables = getCartFocusables();
          if (focusables.length > 0 && !miniCartEl.contains(document.activeElement)) {
            focusables[0].focus();
          }
        }
      });

      overlay.addEventListener('click', () => {
        closeCart(true);
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          closeCart(true);
        });
      }

      removeAllBtn.addEventListener('click', () => {
        // Mostrar loading mientras se limpia el carrito
        showLoading();
        
        fetch('/cart/clear.js', { method: 'POST' })
          .then(res => res.json())
          .then(data => {
            // Limpiar window.CartWithProduct
            window.CartWithProduct = [];
            renderCart(data);
            
            // Publicar evento para sincronizar con cart page
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { 
                source: 'minicart', 
                cartData: data 
              });
            }
          })
          .catch(error => {
            console.error('Error clearing cart:', error);
            hideLoading();
          });
      });

      // Suscribirse al sistema PUB_SUB para actualizar el minicart automáticamente
      // constants.js y pubsub.js se cargan con defer en <head>, minicart.js sin defer en footer
      // Necesitamos esperar a que estén disponibles
      const setupPubSubSubscription = () => {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
            // Ignorar eventos propios para evitar loops
            if (event.source === 'minicart') {
              return;
            }
            
            const miniCart = document.querySelector('.mini-cart');
            const isOpen = miniCart && miniCart.classList.contains('mini-cart--show');
            
            if (isOpen) {
              showLoading();
              await fetchCart();
            } else if (event.source !== 'cart-items') {
              await fetchCart();
            }
          });
          console.log('✅ PUB_SUB subscription active for minicart');
          return true;
        }
        return false;
      };
      
      // Intentar suscribirse inmediatamente
      if (!setupPubSubSubscription()) {
        // Si no está disponible, esperar a que el DOM esté listo
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', setupPubSubSubscription);
        } else {
          // Si el DOM ya está listo, esperar un poco más (scripts defer)
          setTimeout(setupPubSubSubscription, 100);
        }
      }

      // Expuestas para que las aperturas automaticas (tras add-to-cart) no tengan que
      // manipular clases a mano: haciendolo asi se perdian el listener de Escape y el
      // guardado del foco, que solo hace openCart().
      window.openMiniCart = openCart;
      window.closeMiniCart = closeCart;
      window.fetchCart = fetchCart;
      fetchCart();

      // El JS calculaba la altura midiendo .row.top mientras el CSS colocaba el panel
      // en un `top: 97px` escrito a mano. Al no coincidir, en movil el panel se salia
      // por abajo esa diferencia y se comia el total y el boton de checkout. Ademas el
      // marginTop que se ponia aqui lo anulaba un `margin-top: 0 !important` del CSS.
      // Ahora top y height salen del mismo numero.
      function syncMiniCartGeometry() {
        const cartWrap = document.querySelector('.mini-cart');
        if (!cartWrap) return;

        if (window.matchMedia('(max-width: 767px)').matches) {
          const headerFull = document.querySelector('header');
          const borde = headerFull
            ? Math.max(0, Math.round(headerFull.getBoundingClientRect().bottom))
            : 0;
          cartWrap.style.top = `${borde}px`;
          cartWrap.style.height = `calc(100vh - ${borde}px)`;
          cartWrap.style.marginTop = '0px';
        } else {
          const headerEl = document.querySelector('header .row.top');
          const height = headerEl ? headerEl.offsetHeight : 0;
          cartWrap.style.top = '';
          cartWrap.style.height = `calc(100vh - ${height}px)`;
          cartWrap.style.marginTop = `${height}px`;
        }
      }

      syncMiniCartGeometry();
      window.addEventListener('resize', syncMiniCartGeometry);

      // Cerrar al hacer clic fuera.
      //
      // Los widgets flotantes de terceros (el chat de Tidio, el banner de cookies, el
      // modal de quick add) viven en el documento principal y por encima del overlay,
      // asi que sus clics llegaban hasta aqui: al cerrar el chat que se abre solo tras
      // añadir al carrito, ese mismo clic se llevaba por delante el minicart.
      const IGNORAR_AL_CERRAR = [
        '.mini-cart',
        '.content-shopping-cart',
        '.quick-add-modal',
        'csm-cookie-consent',
        '[id*="tidio"]',
        '[class*="tidio"]',
        '[data-keep-minicart-open]'
      ].join(', ');

      document.addEventListener('click', e => {
        if (!e.target || typeof e.target.closest !== 'function') return;
        if (e.target.closest(IGNORAR_AL_CERRAR)) return;
        // El estado lo manda el panel, no la clase del boton (igual que el toggle).
        if (miniCartEl.classList.contains('mini-cart--show')) {
          closeCart(false);
        }
      });
    }
  };



  /**
   * Standard Add-to-Cart Form: handles standalone product form submissions.
   */


  const AddToCartFormModule = {
    init() {
      // ---- Monkeypatch fetch: ahora también escuchamos /cart/add ----
      // Segunda red de seguridad: si alguien vuelve a llamar a init(), no se apila
      // otra capa sobre window.fetch.
      if (window.__minicartFormsInicializado) return;
      window.__minicartFormsInicializado = true;

      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        const url = args[0];

        // Clonamos para poder leer el body sin consumir el stream
        const clonedResponse = response.clone();

        const isAdd = typeof url === 'string' && url.includes('/cart/add');
        const isChange = typeof url === 'string' && (
          url.includes('/cart/change.js') ||
          (url.includes('/cart/update.js') && !url.includes('/cart/update.js')) || // This condition is always false, it should be `!url.includes('/cart/update.js')` for the overall check
          url.includes('/cart/change') ||
          url.includes('/cart/clear.js')
        );

        // NO interceptar las llamadas a productos (evita ciclo infinito)
        const isProductCall = typeof url === 'string' && url.includes('/products/') && url.includes('.js');

        if ((isAdd || (isChange && !url.includes('/cart/update.js'))) && !isProductCall) {
          clonedResponse.text().then(async (text) => {
            try {
              JSON.parse(text); // valida que es JSON
              
              // Debounce para evitar múltiples llamadas rápidas
              // El flag isFetching en fetchCart() previene llamadas simultáneas
              clearTimeout(window._cartUpdateTimeout);
              window._cartUpdateTimeout = setTimeout(async () => {
                if (typeof window.fetchCart === 'function') {
                  await window.fetchCart();
                }
              }, 150); // Debounce corto para actualización rápida
              
              if (isAdd) {
                // Abrir el minicart
                const miniCart = document.querySelector('.mini-cart');
                const overlay = document.querySelector('.overlay');

                if (miniCart && !miniCart.classList.contains('mini-cart--show')) {
                  // Pasar por openCart() y no por las clases: asi el minicart abierto
                  // automaticamente tambien responde a Escape y recuerda el foco previo.
                  if (typeof window.openMiniCart === 'function') {
                    window.openMiniCart();
                  } else {
                    // Respaldo por si MiniCartModule no llego a inicializarse
                    overlay?.classList.add('overlay--show');
                    miniCart.classList.add('mini-cart--show');
                    miniCart.setAttribute('aria-hidden', 'false');
                    const cartTrigger = document.querySelector('.mini-cart__trigger');
                    if (cartTrigger) {
                      cartTrigger.classList.add('active');
                      cartTrigger.setAttribute('aria-expanded', 'true');
                    }
                  }
                }

                // Disparar evento personalizado para que otros módulos lo escuchen
                document.dispatchEvent(new CustomEvent('cart:item-added'));
              }
            } catch (_) { }
          }).catch(() => { });
        }

        return response;
      };

      // ---- helpers ----
      const hasInfiniteOptions = (form) => {
        // Señales típicas del bloque de IO (ShopPad)
        return !!(
          form.querySelector('#infiniteoptions-container') ||
          form.querySelector('input[name="properties[_io_product_bundle_ids]"]') ||
          form.querySelector('input[name^="properties["]')
        );
      };

      // ---- listeners por formulario ----
      const forms = document.querySelectorAll('.add-to-cart-ec-form');
      if (!forms.length) return;

      forms.forEach(form => {
        // Si el formulario tiene Infinite Options, NO lo interceptamos.
        // Dejamos que el plugin haga el add y nosotros reaccionamos vía fetch() anterior.
        if (hasInfiniteOptions(form)) return;

        // Formulario normal (sin IO): enviamos nosotros con todos los campos (FormData → JSON)
        form.addEventListener('submit', e => {
          e.preventDefault();

          const submitBtn = form.querySelector('[type="submit"]');
          const originalText = submitBtn?.innerHTML;
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = 'Adding…';
          }

          // Capturamos TODOS los campos (incluye properties si los hubiera)
          const formData = new FormData(form);
          const formObj = {};
          formData.forEach((value, key) => {
            if (formObj[key]) {
              if (!Array.isArray(formObj[key])) formObj[key] = [formObj[key]];
              formObj[key].push(value);
            } else {
              formObj[key] = value;
            }
          });

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(formObj)
          })
            .then(res => res.json())
            .then(addedItem => {
              // El monkeypatch ya se encarga de actualizar el carrito y llamar fetchCart(),
              // así que aquí no hace falta ningún fetch adicional.

              // Solo abrir el minicart si no está ya abierto
              const miniCart = document.querySelector('.mini-cart');
              if (miniCart && !miniCart.classList.contains('mini-cart--show')) {
                if (typeof window.openMiniCart === 'function') {
                  window.openMiniCart();
                } else {
                  document.querySelector('.overlay')?.classList.add('overlay--show');
                  miniCart.classList.add('mini-cart--show');
                  miniCart.setAttribute('aria-hidden', 'false');
                  const cartTrigger = document.querySelector('.mini-cart__trigger');
                  if (cartTrigger) {
                    cartTrigger.classList.add('active');
                    cartTrigger.setAttribute('aria-expanded', 'true');
                  }
                }
              }
            })
            .catch(err => console.error('Error adding to cart:', err))
            .finally(() => {
              if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = originalText;
              }
            });
        });
      });

      // ---- ejecutar el submit del form original si se da click en el form de la barra ----
      const barform = document.querySelector('.add-to-cart-ec-form-bar');

      if (barform) {
        barform.addEventListener('submit', e => {
          e.preventDefault();
          const originalForm = document.querySelector('.add-to-cart-ec-form');
          if (originalForm) {
            const originalSubmitBtn = originalForm.querySelector('[type="submit"]');
            if (originalSubmitBtn) {
              originalSubmitBtn.click(); // Dispara el submit del form original
            }
          }
        });
      }


    }
  };


  // quick-add-modal.js lanza un DOMContentLoaded SINTETICO cada vez que se cierra el
  // modal (reinitializeMainProductEvents). Sin este guardia, cada cierre volvia a
  // ejecutar los tres init(): el icono del carrito acababa con dos listeners de click
  // — uno abria y el siguiente cerraba en el mismo clic, asi que "no abria" — y el
  // parche de window.fetch se apilaba sobre si mismo, disparando varios fetchCart()
  // por peticion (de ahi el "Fetch ya en progreso, saltando..." repetido).
  let modulosInicializados = false;
  document.addEventListener('DOMContentLoaded', () => {
    if (modulosInicializados) return;
    modulosInicializados = true;

    FBTModule.init();
    MiniCartModule.init();
    AddToCartFormModule.init();
  });
})();
